import { QuestionCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_IDEA_METRIC_TRIGGER,
  GET_CHANNEL_BY_ID,
  LIST_METRIC_TRIGGERS,
  MetricTrigger,
  Phase,
} from '@frontend/data-access';
import { App, Button, Form, Select, Tooltip, Typography } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { TabHeader } from '../components';

const { Text } = Typography;

const Reminder = () => {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [targetPhase, setTargetPhase] = useState<string>();
  const [actualPhase, setActualPhase] = useState<string>();
  const router = useRouter();
  const { id } = router.query;

  const { data: channelData, loading: channelLoading } = useQuery<{
    channel: {
      channelPhases: {
        phase: Phase;
      }[];
    };
  }>(GET_CHANNEL_BY_ID, {
    variables: { id },
    skip: !id,
  });

  const { loading: triggerLoading } = useQuery<{
    listMetricTrigger: MetricTrigger[];
  }>(LIST_METRIC_TRIGGERS, {
    onCompleted: (data) => {
      const targetTrigger = data.listMetricTrigger.find(
        (trigger) => trigger.type.toLowerCase() === 'idea_metric_target'
      );
      const actualTrigger = data.listMetricTrigger.find(
        (trigger) => trigger.type.toLowerCase() === 'idea_metric_result'
      );

      setTargetPhase(targetTrigger?.phaseId);
      setActualPhase(actualTrigger?.phaseId);
      form.setFieldsValue({
        targetPhase: targetTrigger?.phaseId,
        actualPhase: actualTrigger?.phaseId,
      });
    },
  });

  const [createMetricTrigger, { loading: saveLoading }] = useMutation(
    CREATE_IDEA_METRIC_TRIGGER,
    {
      onCompleted: () => {
        notification.success({
          message: t('metric_reminder_settings_saved_successfully'),
        });
      },
      onError: (error) => {
        notification.error({
          message: t('error_saving_metric_reminder_settings'),
          description: error.message,
        });
      },
      refetchQueries: [{ query: LIST_METRIC_TRIGGERS }],
    }
  );

  const phases = useMemo(
    () =>
      channelData?.channel.channelPhases.map((cp) => ({
        label: cp.phase.title,
        value: cp.phase.id,
      })) || [],
    [channelData?.channel.channelPhases]
  );

  const handleSave = () => {
    createMetricTrigger({
      variables: {
        targetPhase: targetPhase || null,
        resultPhase: actualPhase || null,
      },
    });
  };

  const loading = channelLoading || triggerLoading;

  const { t } = useTranslation('common');

  return (
    <div className="space-y-6">
      <TabHeader
        title={t('metric_reminder')}
        description={t('metric_reminder_description')}
      />

      <Form form={form} layout="vertical" onFinish={handleSave}>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label={
              <div className="flex items-center gap-2">
                <Text className="!font-medium">{t('target_metric')}</Text>
                <Tooltip title={t('target_metric_tooltip')}>
                  <span className="cursor-help text-gray-900">
                    <QuestionCircleOutlined />
                  </span>
                </Tooltip>
              </div>
            }
            name="targetPhase"
          >
            <Select
              placeholder={t('no_phase')}
              loading={loading}
              options={phases}
              value={targetPhase}
              allowClear
              onChange={(value) => {
                setTargetPhase(value);
                // Clear actual phase if it's the same as new target
                if (value === actualPhase) {
                  setActualPhase(undefined);
                  form.setFieldValue('actualPhase', undefined);
                }
              }}
              className="w-full"
            />
          </Form.Item>
          <Form.Item
            label={
              <div className="flex items-center gap-2">
                <Text className="!font-medium">{t('actual_metric')}</Text>
                <Tooltip title={t('actual_metric_tooltip')}>
                  <span className="cursor-help text-gray-900">
                    <QuestionCircleOutlined />
                  </span>
                </Tooltip>
              </div>
            }
            name="actualPhase"
          >
            <Select
              placeholder={t('no_phase')}
              loading={loading}
              options={phases}
              value={actualPhase}
              allowClear
              onChange={setActualPhase}
              className="w-full"
            />
          </Form.Item>
        </div>

        <Button
          shape="round"
          type="default"
          htmlType="submit"
          loading={saveLoading}
        >
          {t('save')}
        </Button>
      </Form>
    </div>
  );
};

export default Reminder;
